import React, { useState } from "react";
import { Modal, Input, InputNumber, message, Form } from "antd";
import { useNavigate } from "react-router-dom";
import { RepairJob } from "../../../Types/RepairJobTypes";
import { TemporaryItemProduct } from "../../../Enums/TemporaryItem";
import { useCart } from "../../../Context/useCartContext";
import { ROUTE } from "../../../Constant";
import { modalTitle, modalFooter } from "../../../components";

interface RepairJobPaymentModalProps {
  open: boolean;
  onClose: () => void;
  job: RepairJob;
}

const RepairJobPaymentModal: React.FC<RepairJobPaymentModalProps> = ({
  open,
  onClose,
  job,
}) => {
  const navigate = useNavigate();
  const { addToCart, setRepairJobId, setRepairJobAdvance, setRepairJobPaymentMethodId } = useCart();

  const deviceLabel = [job.device_brand, job.device_model].filter(Boolean).join(" ") || job.device_type;

  const [itemName, setItemName] = useState(
    `Repair: ${deviceLabel} (#${job.job_number})`,
  );
  const [totalCost, setTotalCost] = useState<number>(Number(job.total_cost) || 0);
  const [sellingPrice, setSellingPrice] = useState<number>(
    Number(job.selling_price) || 0,
  );
  const [notes, setNotes] = useState("");

  const handleAddToCart = () => {
    if (!itemName.trim()) {
      message.error("Please enter an item name.");
      return;
    }
    if (sellingPrice <= 0) {
      message.error("Selling price must be greater than 0.");
      return;
    }

    const dueAmount = Math.max(0, sellingPrice - (Number(job.paid_amount) || 0));

    setRepairJobId(job.id);
    setRepairJobAdvance(0);
    setRepairJobPaymentMethodId(null);

    const baseName = itemName.trim();
    const descText = [`Job #${job.job_number}`, `Issues: ${job.reported_issues}`, notes.trim()]
      .filter(Boolean)
      .join(" | ");

    addToCart(
      {
        id: TemporaryItemProduct.PRODUCT,
        title: `${baseName}${dueAmount < sellingPrice ? ' - Due Payment' : ''}`,
        image: "",
        is_tempory_item: true,
        is_repair_item: true,
        repair_job_id: job.id,
        is_balance: true,
      },
      {
        batch_no: `${TemporaryItemProduct.STOCK}-balance-${job.id}`,
        cost_price: totalCost,
      },
      dueAmount,
      1,
      { is_tempory_item: true, is_repair_item: true },
      totalCost,
      descText || `Due payment for Job #${job.job_number}`
    );

    message.success(`${baseName} added to cart.`);
    onClose();
    navigate(`/system/${ROUTE.sales_billing}`);
  };

  return (
    <Modal
      title={modalTitle(`Proceed to Pay - ${job.job_number}`)}
      open={open}
      onCancel={onClose}
      footer={modalFooter({
        actions: [
          {
            label: "Cancel",
            onClick: onClose,
          },
          {
            label: "Add to Cart & Go to POS",
            type: "primary",
            onClick: handleAddToCart,
            className: "min-w-[180px]",
          },
        ],
      })}
      width={520}
      destroyOnClose
    >
      <Form layout="vertical" className="py-2">
        <Form.Item label="Item Name">
          <Input
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            placeholder="Enter item name"
          />
        </Form.Item>

        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item label="Total Cost (Rs.)">
            <InputNumber
              className="w-full"
              value={totalCost}
              onChange={(val) => setTotalCost(val ?? 0)}
              min={0}
              precision={2}
            />
          </Form.Item>
          <Form.Item label="Selling Price (Rs.)">
            <InputNumber
              className="w-full"
              value={sellingPrice}
              onChange={(val) => setSellingPrice(val ?? 0)}
              min={0}
              precision={2}
            />
          </Form.Item>
        </div>

        <Form.Item label="Extra Details (optional)">
          <Input.TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this repair job..."
            rows={3}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RepairJobPaymentModal;
